import { useGetProductSelectionDataQuery } from "@lending/hooks/queries/ProductSelection/useGetProductSelectionDataQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { type ProductType, useFlowNavigation } from "@lending/navigation";
import { isMlaQuestionEnabled } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";
import { useLendingHost } from "../../../contexts/LendingHostContext";

const LOAN_PORTAL_FLOW = "loan_portal";

function useProductSelectionContainer() {
  const { startFlow } = useFlowNavigation();
  const { authMode } = useLendingHost();
  const { institution } = useGetInstitutionQuery();
  const hasLendingQuestions = isMlaQuestionEnabled(institution.lending_ask_additional_fields);

  const {
    creditCardProductsData,
    personalLoanProductsData,
    autoLoanProductsData,
    hasEligibilityCriteria,
  } = useGetProductSelectionDataQuery();

  // Get the number of products available for each type
  const numPersonalLoanProducts = personalLoanProductsData.count;
  const numCreditCardProducts = creditCardProductsData.count;
  const numAutoLoanProducts = autoLoanProductsData.count;

  // Determine whether to show each product option
  const showPersonalLoanOption = numPersonalLoanProducts > 0;
  const showCreditCardOption = numCreditCardProducts > 0;
  const showAutoLoanOption = numAutoLoanProducts > 0;

  // Returning applicants can sign in to the Loan Portal instead of starting a
  // new application. Hidden when hosted: this button opens the portal's login,
  // which proves identity by email and must never front a user Online Banking has
  // already authenticated. Hosted users reach the portal by its own entry point.
  // Gate it here too so the product-selection page and the redirects that count
  // options stay in agreement.
  const showLoanPortalOption =
    authMode !== "digital_banking" && institution.lending_flows_enabled.includes(LOAN_PORTAL_FLOW);

  function onClickProduct(productType: ProductType) {
    const hasMultipleCreditCardProducts = numCreditCardProducts > 1;
    startFlow(productType, {
      hasMultipleCreditCardProducts,
      hasEligibilityCriteria,
      hasLendingQuestions,
    });
  }

  return {
    onClickProduct,
    showPersonalLoanOption,
    showCreditCardOption,
    showAutoLoanOption,
    showLoanPortalOption,
    hasEligibilityCriteria,
  };
}

export { useProductSelectionContainer };
